import { useState, type ReactElement } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  Briefcase,
  // DownloadCloud, // temporarily unused — Import from FHIR launcher hidden
  LayoutGrid,
  Plus,
  Search,
  Settings,
  User,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/auth/useAuth';
import { useMe } from '@/hooks/useMe';
import { usePermissions } from '@/auth/usePermissions';
import { cn } from '@/lib/utils';
import { isApplePlatform } from '@/lib/platform';
import { patientInitials } from '@/api/mappers';
import { ImportFromFhirDialog } from '@/features/fhir-launcher/components/ImportFromFhirDialog';
import { ThemeMenu } from '@/theme/ThemeMenu';

import { Breadcrumbs } from './Breadcrumbs';
import { MockIdentitySwitcher } from './MockIdentitySwitcher';

interface TopNavProps {
  /** Forward-compat: white-label builds wire a tenant logo here. */
  readonly tenantLogoSrc?: string | null;
  readonly tenantName?: string | null;
  readonly onOpenQuickSearch?: (() => void) | undefined;
}

export function TopNav({
  onOpenQuickSearch,
}: TopNavProps): ReactElement {
  const { t } = useTranslation();
  const auth = useAuth();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const {
    canBrowsePatients,
    canCreatePatients,
    canCreateFamilies,
    canOpenAdmin,
    canOpenPlatform,
  } = usePermissions();
  const [importFromFhirOpen, setImportFromFhirOpen] = useState(false);

  // PTV2-1383: a global-plane admin sees the Global Cluster Administration shell — clinical nav is
  // hidden, the logo returns to the platform console, and the bar centre shows the title. The signal
  // is platform:* (exclusive to global-plane roles). NOTE a PLATFORM_ADMIN also has admin:*/tenant:*
  // per the ABAC policy, so the tenant/admin affordances below must be gated with `!isGlobalAdmin`
  // explicitly — they do NOT auto-hide via their own permission gates.
  const isGlobalAdmin = canOpenPlatform;

  const userInitials =
    me !== undefined ? patientInitials(me.firstName, me.lastName) : null;

  return (
    <header data-surface="topbar" className="bg-brand text-brand-foreground">
      <nav
        aria-label={t('shell.topNav.ariaLabel')}
        className="h-12 flex items-center px-4 gap-4"
      >
        <Link
          to={isGlobalAdmin ? '/platform' : '/'}
          className="flex items-center shrink-0 rounded-md px-1 py-1 -mx-1 hover:bg-white/10 transition-colors"
          aria-label={t('shell.topNav.home')}
        >
          <img
            src="/assets/phenotips-logo-white.svg"
            alt={t('shell.rail.brandName')}
            className="h-6 w-auto"
          />
        </Link>

        <div className="w-px h-5 bg-white/20 shrink-0" aria-hidden="true" />

        {/* Primary destinations. Administration (routed by permission — platform console for a
            cluster admin, tenant admin otherwise) sits in the nav where Browse would be; Browse is
            the shared cohort view; My Cases is the signed-in user's personal task inbox (PTV2-1236). */}
        <div className="flex items-center gap-1 shrink-0">
          {(canOpenPlatform || canOpenAdmin) && (
            <NavLink
              to={canOpenPlatform ? '/platform' : '/admin'}
              className={({ isActive }) => primaryNavClass(isActive)}
            >
              <Settings className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline">{t('shell.topNav.admin')}</span>
            </NavLink>
          )}
          {canBrowsePatients && !isGlobalAdmin && (
            <NavLink
              to="/browse/patients"
              className={({ isActive }) => primaryNavClass(isActive)}
            >
              <LayoutGrid className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline">{t('shell.topNav.browse')}</span>
            </NavLink>
          )}
          {auth.user !== null && !isGlobalAdmin && (
            <NavLink
              to="/my-cases"
              className={({ isActive }) => primaryNavClass(isActive)}
            >
              <Briefcase className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline">{t('shell.topNav.myCases')}</span>
            </NavLink>
          )}
        </div>

        <div className="w-px h-5 bg-white/20 shrink-0" aria-hidden="true" />

        <div className="flex-1 min-w-0">
          <Breadcrumbs />
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <MockIdentitySwitcher />
          {canBrowsePatients && !isGlobalAdmin && onOpenQuickSearch !== undefined && (
            <button
              type="button"
              onClick={onOpenQuickSearch}
              aria-label={t('shell.topNav.openSearch')}
              className={cn(
                'hidden md:flex w-56 lg:w-72 h-8 items-center gap-2 px-3 rounded-md text-[12.5px]',
                'bg-white/10 text-white/80 border border-white/20',
                'hover:bg-white/15 hover:text-white hover:border-white/30',
                'transition-colors cursor-pointer select-none mr-1',
              )}
            >
              <Search className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="flex-1 text-left truncate">
                {t('shell.topNav.searchPlaceholder')}
              </span>
              <kbd className="font-mono text-[10.5px] bg-white/10 border border-white/20 rounded px-1 py-px text-white/80">
                {t(
                  isApplePlatform()
                    ? 'shell.topNav.searchShortcutMac'
                    : 'shell.topNav.searchShortcutOther',
                )}
              </kbd>
            </button>
          )}

          {(canCreatePatients || canCreateFamilies) && !isGlobalAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t('shell.topNav.create')}
                  className="text-white/75 hover:text-white hover:bg-white/10"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canCreatePatients && (
                  <DropdownMenuItem onSelect={() => { void navigate('/patients/new'); }}>
                    {t('shell.topNav.newPatient')}
                  </DropdownMenuItem>
                )}
                {canCreateFamilies && (
                  <DropdownMenuItem onSelect={() => { void navigate('/families/new'); }}>
                    {t('shell.topNav.newFamily')}
                  </DropdownMenuItem>
                )}
                {/* Import from FHIR launcher temporarily hidden.
                {canCreatePatients && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={() => {
                        setImportFromFhirOpen(true);
                      }}
                    >
                      <DownloadCloud className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                      {t('shell.topNav.importFromFhir')}
                    </DropdownMenuItem>
                  </>
                )}
                */}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {importFromFhirOpen && (
            <ImportFromFhirDialog
              onClose={() => {
                setImportFromFhirOpen(false);
              }}
            />
          )}

          {/* PTV2-1383: the platform/admin entry points now live as the "Administration" nav link on
              the left (routed by permission); the old right-side icon buttons were redundant. */}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('shell.topNav.profile')}
                className="rounded-full h-8 w-8 hover:bg-white/10"
              >
                {userInitials !== null && userInitials.length > 0 ? (
                  <span className="h-7 w-7 rounded-full bg-white/15 text-white flex items-center justify-center text-xs font-semibold leading-none">
                    {userInitials}
                  </span>
                ) : (
                  <span className="h-7 w-7 rounded-full bg-white/10 text-white/80 flex items-center justify-center">
                    <User className="h-4 w-4" aria-hidden="true" />
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-48">
              {auth.user !== null && (
                <>
                  <DropdownMenuLabel className="font-normal py-2">
                    <p className="text-xs text-muted-foreground leading-none mb-1">
                      {t('auth.signedInAs', { name: '' }).replace(/ $/, '')}
                    </p>
                    <p className="text-sm font-medium truncate">{auth.user.name}</p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                </>
              )}
              <ThemeMenu />
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => { void navigate('/profile'); }}>
                {t('auth.profile')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => {
                  auth.signOut().catch(/* noop */ () => {
                    /* intentional */
                  });
                }}
              >
                {t('auth.signOut')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>
    </header>
  );
}

/** Shared class for the brand-bar primary destination links. */
function primaryNavClass(isActive: boolean): string {
  return cn(
    'flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[13px] font-medium transition-colors',
    isActive
      ? 'text-white bg-white/15'
      : 'text-white/70 hover:text-white hover:bg-white/10',
  );
}
